<template>
  <v-dialog
    scrollable
    max-width="650"
    v-model="show"
    data-testid="challenge-modal"
    @click:outside="$emit('close')"
    @keydown.esc="$emit('close')"
  >
    <v-card class="ma-0 px-6 py-4" v-if="show">
      <v-card-title class="ma-0 pa-0 mb-4 h6">
        {{ modalTitle }}
      </v-card-title>
      <v-card-text class="ma-0 pa-0">
        <v-form v-model="isFormValid" ref="challengeForm">
          <MultipleLocationSelector
            class="mt-1"
            data-tourid="challengeForm-locationSelector"
            :location="form.location"
            @change="locationSelectorChange"
          />
          <v-text-field
            v-model="form.name"
            outlined
            dense
            id="challenge_name"
            name="challenge_name"
            type="text"
            data-testid="challengeForm-input-name"
            data-tourid="challengeForm-input-name"
            :rules="rules"
            :label="`${$t('challenge.name_of_the_challenge')}*`"
            :maxlength="90"
          />
          <CategoriesSelector
            data-private
            class="mt-1"
            v-if="form.location.sites.length > 0"
            data-testid="challengeForm-dropdown-categories"
            :hideDetails="false"
            :value="form.categories"
            :siteIds="form.location.sites"
            @input="form.categories = $event"
          />
          <DurationPicker
            class="mt-1"
            required
            :value="duration"
            @change="changeDuration"
          />

          <v-text-field
            dense
            outlined
            id="KPIs"
            data-testid="challengeForm-input-kpis"
            name="KPIs"
            type="text"
            v-model="form.KPIs"
            :label="$t('challenge.kpi_key_performance_indicator')"
          />
          <v-textarea
            v-model="form.description"
            data-private
            dense
            outlined
            auto-grow
            no-resize
            rows="4"
            cols="33"
            required
            data-testid="challengeForm-textarea-description"
            data-tourid="challengeForm-textarea-description"
            :rules="rules"
            :label="`${$t('challenge.description')}*`"
          />
          <v-textarea
            v-model="form.rewardsText"
            data-private
            dense
            outlined
            auto-grow
            no-resize
            rows="4"
            cols="33"
            data-testid="challengeForm-textarea-rewards"
            :label="$t('challenge.rewards')"
            :value="form.rewardsText"
          />
        </v-form>
      </v-card-text>
      <v-card-actions class="justify-end ma-0 pa-0 mt-4">
        <Button
          plain
          class="me-2"
          color="grey"
          data-testid="challenges_modal_cancel"
          data-tourid="challenge-button-cancel"
          :text="$t('common.button.cancel')"
          @click="$emit('close')"
        />

        <Button
          color="primary"
          data-testid="challenges_modal_success"
          data-tourid="challenge-button-create"
          :text="modalOkText"
          @click="onSubmit"
        />
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import _ from 'lodash'
import { mapActions, mapGetters } from 'vuex'
import api from '@/api'

import mixin from '@/mixins/mixin.vue'

import Button from '@/components/UI/Button'

import CategoriesSelector from '@/components/CategoriesSelector.vue'
import MultipleLocationSelector from '@/components/LocationSelector/MultipleLocationSelector.vue'
import DurationPicker from '@/components/ChallengeModal/DurationPicker.vue'

export default {
  name: 'ChallengeModal',

  mixins: [mixin],

  components: {
    Button,
    CategoriesSelector,
    MultipleLocationSelector,
    DurationPicker,
  },

  props: {
    show: {
      type: Boolean,
      required: true,
    },
    challenge: {
      type: Object,
      required: false,
    },
  },

  data() {
    return {
      loading: false,
      rules: [
        field =>
          !!field.trim() || this.$t('validation.form.this_field_is_required'),
      ],
      isFormValid: null,
      form: {
        name: '',
        description: '',
        KPIs: '',
        rewardsText: '',
        categories: [],
        location: {
          organization: '',
          sites: [],
          tags: [],
        },
        startAt: null,
        endAt: null,
      },
    }
  },

  async mounted() {
    if (!this.isEditMode) this.form.location = this.accountLocation
  },

  watch: {
    challenge(challenge) {
      if (!challenge._id) return

      this.form = {
        ...challenge,
        location: {
          organization: challenge.organization,
          sites: challenge?.sites?.map?.(site => site._id),
          tags: challenge?.tags ?? [],
        },
      }
    },
  },

  computed: {
    ...mapGetters(['account', 'organizationId']),

    duration() {
      return _.compact([this.form?.startAt, this.form?.endAt])
    },

    accountLocation() {
      return {
        organization: this.account?.organization?._id,
        sites: [this.account?.site?._id],
        tags: [],
      }
    },

    isEditMode() {
      return Boolean(this.challenge?._id)
    },

    singleSiteSelected() {
      return this.form?.location?.sites?.length === 1
    },

    modalTitle() {
      if (this.isEditMode) return this.$t('challenge.edit_challenge')
      return this.$t('challenge.create_challenge')
    },

    modalOkText() {
      if (this.isEditMode) return this.$t('common.button.save')
      return this.$t('common.button.create')
    },
  },

  methods: {
    ...mapActions({
      getChallenge: 'getChallenge',
    }),

    changeDuration(duration) {
      this.form.startAt = duration[0]
      this.form.endAt = duration[1]
    },

    locationSelectorChange(location) {
      this.form.location = {
        ...location,
        organization: this.accountLocation.organization,
      }
    },

    onSubmit() {
      this.$refs.challengeForm.validate()

      if (!this.isFormValid) return

      const payload = this.buildPayload()
      if (this.isEditMode) {
        this.updateChallenge(payload)
      } else {
        this.createChallenge(payload)
        this.$emit('created')
      }
      return this.$emit('close')
    },

    buildPayload() {
      return {
        ...this.challenge,
        ...this.form,

        sites: this.form.location.sites,
        tags: this.form?.location?.tags ?? [],
      }
    },

    async createChallenge(payload) {
      try {
        this.loading = true
        const response = await api.challenge.create(
          this.organizationId,
          payload
        )

        this.showSuccess(this.$t('challenge.the_challenge_has_been_created'))
        this.navigateTo(
          `/challenge/${this.organizationId}/${response.data._id}`
        )
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        this.loading = false
      }
    },

    async updateChallenge(payload) {
      try {
        this.loading = true
        await api.challenge.update(
          this.organizationId,
          this.challenge._id,
          payload
        )

        this.showSuccess(
          this.$t('challenge.the_challenge_was_updated_successfully')
        )
        await this.getChallenge({
          organizationId: this.organizationId,
          challengeId: this.challenge._id,
        })
        this.$emit('close')
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        this.loading = false
      }
    },
  },
}
</script>
